import React from "react";
import { calendlyUrl } from "@/lib/analytics";

/* ==========================================================================
 * Homepage enhancement sections.
 * Purely additive — none of the existing components are touched.
 * Two exported components:
 *   <HomeEnhancementsTop/>    → renders after <Solutions/>
 *   <HomeEnhancementsClose/>  → renders after <About/>
 * All copy from the client brief. Uses existing design tokens only.
 * ========================================================================== */

const bookStrategy = () => window.open(calendlyUrl(), "_blank", "noopener,noreferrer");

/* ------------------------- SECTION 1: HOW AI HELPS ------------------------ */

const AI_EMPLOYEE_IMG = "https://customer-assets-lqy194kg.emergentagent.net/job_creative-freedom-ai-1/artifacts/dc0582f64f4a925b_AI%20Employee%20Gold%20Network.png";

function WorkflowIllustration() {
  return (
    <div className="relative w-full max-w-md mx-auto" data-testid="workflow-illustration">
      <img
        src={AI_EMPLOYEE_IMG}
        alt="AI Employee by Freedom By AI Design connecting AI Receptionist, CRM Integration, Business Automations, Website + AI Chatbot, Workflow Integration, and AI-Powered Lead Generation"
        className="w-full h-auto"
        loading="lazy"
        decoding="async"
      />
    </div>
  );
}

const HOW_AI_CARDS = [
  {
    title: "Never Miss Another Opportunity",
    body: "Your AI Receptionist answers every phone call, SMS message, Facebook Messenger conversation, Instagram DM, website chat, and WhatsApp inquiry for international businesses. Every customer receives an immediate, professional response, even when you're busy or closed.",
  },
  {
    title: "Book More Appointments",
    body: "Your AI schedules consultations, estimates, reservations, appointments, and meetings automatically. Customers can book with your business 24 hours a day, even after normal business hours.",
  },
  {
    title: "Delight Your Customers",
    body: "Provide fast, friendly answers to frequently asked questions including business hours, pricing, directions, services, policies, and more. Every interaction feels natural, professional, and consistent.",
  },
  {
    title: "Save Hours Every Week",
    body: "Stop spending valuable time answering repetitive questions, scheduling appointments, and following up manually. Your AI employees handle routine communication so you can focus on serving customers, leading your team, and growing your business.",
  },
];

function HowAIHelps() {
  return (
    <section id="how-ai-helps" className="py-24 md:py-32" data-testid="section-how-ai-helps">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="overline">How AI Helps Your Business</div>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
            Spend Less Time <em className="text-brand">Managing</em> Your Business.
            <br />Spend More Time <em className="text-brand">Growing</em> It.
          </h2>
          <p className="text-secondary text-lg mt-8 leading-relaxed">
            Your AI employees work behind the scenes to answer customers, book appointments,
            automate repetitive tasks, and provide exceptional service twenty-four hours a day.
          </p>
          <p className="text-muted text-base mt-6 leading-relaxed max-w-2xl">
            You didn&apos;t start your business to spend your day answering phones, replying to messages,
            scheduling appointments, or handling repetitive administrative work. You started your
            business because you&apos;re passionate about serving your customers and growing something
            meaningful. Freedom By AI Design creates intelligent AI employees that handle the
            repetitive work, allowing you and your team to focus on the work that only humans can do.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mt-20 items-start">
          <div className="lg:col-span-5">
            <WorkflowIllustration />
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
            {HOW_AI_CARDS.map((c, i) => (
              <div key={c.title} className="editorial-card p-8" data-testid={`how-ai-card-${i}`}>
                <div className="overline">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-2xl mt-3">{c.title}</h3>
                <p className="text-secondary text-sm mt-4 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* -------------------- SECTION 2: MEET YOUR AI EMPLOYEES ------------------- */

const AI_EMPLOYEES = [
  {
    title: "AI Receptionist",
    role: "First Impressions & Live Response",
    body: "Welcomes every customer with professionalism, answers calls, books appointments, and ensures no opportunity is missed.",
  },
  {
    title: "AI Appointment Coordinator",
    role: "Calendar & Scheduling",
    body: "Keeps your calendar organized by scheduling, rescheduling, confirming, and reminding customers about upcoming appointments.",
  },
  {
    title: "AI Customer Experience Specialist",
    role: "Omnichannel Support",
    body: "Responds instantly to common questions across multiple communication channels, providing fast and consistent service.",
  },
  {
    title: "Business Automation Assistant",
    role: "Behind-the-Scenes Operations",
    body: "Handles repetitive administrative tasks behind the scenes so your team can focus on higher-value work.",
  },
];

function MeetAIEmployees() {
  return (
    <section id="meet-ai" className="py-24 md:py-32 bg-surface" data-testid="section-meet-ai">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <div className="overline">Meet Your AI Employees</div>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
            A new kind of <em className="text-brand">team member</em>.
          </h2>
          <p className="text-secondary text-lg mt-8 leading-relaxed">
            Not software. Not another tool. Think of them as valued members of your team —
            each with a specific role, each working around the clock to serve your customers
            and support your growth.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-16">
          {AI_EMPLOYEES.map((e, i) => (
            <div key={e.title} className="p-10 border border-white/10 hover:border-brand-hi transition-colors duration-500 group" data-testid={`ai-employee-${i}`}>
              <div className="flex items-baseline justify-between gap-4">
                <div className="font-display text-3xl group-hover:text-brand transition-colors duration-500">{e.title}</div>
                <div className="overline text-brand shrink-0">{String(i + 1).padStart(2, "0")}</div>
              </div>
              <div className="mt-3 text-muted text-xs uppercase tracking-widest">{e.role}</div>
              <p className="text-secondary mt-6 leading-relaxed">{e.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------------- SECTION 3: BEFORE & AFTER ------------------------- */

const BEFORE = [
  "Missed phone calls",
  "Lost appointments",
  "Staff constantly interrupted",
  "Slow customer responses",
  "Administrative overload",
  "Customers waiting for replies",
];
const AFTER = [
  "Every call answered",
  "Appointments booked automatically",
  "Immediate customer responses",
  "More organized workflows",
  "More productive employees",
  "Better customer experiences",
  "More time to focus on growing the business",
];

function BeforeAfter() {
  return (
    <section id="before-after" className="py-24 md:py-32" data-testid="section-before-after">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <div className="overline">Before &amp; After</div>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
            The difference is <em className="text-brand">felt</em> from day one.
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mt-16">
          <div className="p-10 border border-white/10 bg-black/20" data-testid="before-column">
            <div className="overline text-muted">Before Freedom By AI Design</div>
            <ul className="mt-8 space-y-4">
              {BEFORE.map((line) => (
                <li key={line} className="flex gap-4 items-start text-secondary">
                  <span className="mt-2 w-4 h-px bg-white/20 shrink-0" />
                  <span className="line-through decoration-white/20">{line}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="p-10 border border-brand bg-black/40 relative" data-testid="after-column">
            <div className="absolute -top-3 left-10 px-3 py-1 bg-black overline text-brand">With AI Employees</div>
            <div className="overline text-brand mt-2">After Freedom By AI Design</div>
            <ul className="mt-8 space-y-4">
              {AFTER.map((line) => (
                <li key={line} className="flex gap-4 items-start text-primary">
                  <span className="mt-2 w-4 h-px bg-brand shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* --------------------- SECTION 4: 4-STEP PROCESS -------------------------- */

const STEPS = [
  {
    title: "Schedule Your AI Strategy Session",
    body: "We'll learn about your business, how you currently operate, and where you're spending unnecessary time.",
  },
  {
    title: "Custom AI Solution Design",
    body: "We design AI employees specifically for your business, your customers, and your workflow.",
  },
  {
    title: "Implementation",
    body: "We build, configure, test, and integrate your AI systems with your existing business processes.",
  },
  {
    title: "Launch & Ongoing Support",
    body: "Once your AI employees are live, we continue supporting, optimizing, and improving your system as your business grows.",
  },
];

function ProcessSteps() {
  return (
    <section id="process" className="py-24 md:py-32 bg-surface" data-testid="section-process">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <div className="overline">Working Together</div>
          <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
            Working Together Is <em className="text-brand">Simple</em>.
          </h2>
        </div>

        <ol className="mt-16 space-y-0 border-t border-white/10">
          {STEPS.map((s, i) => (
            <li key={s.title} className="grid grid-cols-1 md:grid-cols-12 gap-6 py-10 border-b border-white/10 group hover:bg-black/30 transition-colors duration-500" data-testid={`process-step-${i + 1}`}>
              <div className="md:col-span-2">
                <div className="font-display text-6xl md:text-7xl text-brand leading-none">{String(i + 1).padStart(2, "0")}</div>
              </div>
              <div className="md:col-span-4">
                <div className="overline">Step {i + 1}</div>
                <h3 className="font-display text-2xl md:text-3xl mt-3">{s.title}</h3>
              </div>
              <div className="md:col-span-6 md:pl-10 md:border-l md:border-white/10">
                <p className="text-secondary leading-relaxed">{s.body}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* -------- SECTION 5: EMOTIONAL CLOSE + FINAL CTA --------------------------- */

function EmotionalClose() {
  return (
    <section id="emotional-close" className="py-24 md:py-32 relative overflow-hidden" data-testid="section-emotional-close">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute -top-20 -right-20 w-[520px] h-[520px] rounded-full opacity-20"
             style={{ background: "radial-gradient(closest-side, #D4A24A, transparent)" }} />
        <div className="absolute -bottom-40 -left-20 w-[420px] h-[420px] rounded-full opacity-10"
             style={{ background: "radial-gradient(closest-side, #E8BC65, transparent)" }} />
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 relative">
        <div className="overline">A Note From Freedom By AI Design</div>
        <h2 className="font-display font-light text-4xl md:text-6xl tracking-tighter leading-[1.05] mt-6">
          You Built Your Business <em className="text-brand">For A Reason</em>.
        </h2>
        <div className="mt-10 space-y-6 text-primary text-lg leading-relaxed">
          <p>You didn&apos;t build your business to spend every day tied to the phone.</p>
          <p>You built it because you&apos;re passionate about serving people, creating something meaningful, and making an impact.</p>
          <p className="text-brand-hi italic font-display text-2xl md:text-3xl leading-snug pt-4">
            Let your AI employees handle the repetitive work while you focus on what you do best.
          </p>
        </div>

        <div className="mt-16 pt-12 border-t border-brand">
          <div className="overline">Ready to Begin</div>
          <h3 className="font-display font-light text-3xl md:text-5xl mt-4 leading-tight">
            Ready To Add AI Employees To Your Team?
          </h3>
          <p className="text-secondary text-lg mt-6 max-w-2xl leading-relaxed">
            Let&apos;s build an intelligent business that serves your customers, saves you time, and
            supports your growth around the clock.
          </p>
          <button
            type="button"
            onClick={bookStrategy}
            data-calendly
            className="btn-primary mt-10"
            data-testid="final-cta-schedule"
          >
            Schedule Your AI Strategy Session →
          </button>
        </div>
      </div>
    </section>
  );
}

/* ==========================================================================
 * Composed exports
 * ========================================================================== */

export function HomeEnhancementsTop() {
  return (
    <>
      <HowAIHelps />
      <MeetAIEmployees />
    </>
  );
}

export function HomeEnhancementsClose() {
  return (
    <>
      <BeforeAfter />
      <ProcessSteps />
      <EmotionalClose />
    </>
  );
}
